import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { TextArea } from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserUseCaseStep } from "lib/models/userusecase-models";
import React, { FC } from "react";

import { STEP_VALIDATORS } from "./form-validation";
import { FormField, FormFieldConfig } from "./FormField";
import { StepActions } from "./StepActions";
import { StepProps } from "./StepProps";

const WORKFLOW_FIELDS: FormFieldConfig[] = [
  {
    id: "before-workflow",
    name: "beforeWorkflow",
    type: "textarea",
    labelKey: "useCases.contributeForm.labelBeforeWorkflow",
    placeholderKey: "useCases.contributeForm.beforeWorkflowPlaceholder",
    required: true,
  },
  {
    id: "after-workflow",
    name: "afterWorkflow",
    type: "textarea",
    labelKey: "useCases.contributeForm.labelAfterWorkflow",
    placeholderKey: "useCases.contributeForm.afterWorkflowPlaceholder",
    required: true,
  },
];

const STEP_FIELD_CONFIGS: {
  name: keyof UserUseCaseStep;
  labelKey: string;
  placeholderKey: string;
  required?: boolean;
  multiline?: boolean;
}[] = [
  {
    name: "codeSnippet",
    labelKey: "useCases.contributeForm.labelCodeSnippet",
    placeholderKey: "useCases.contributeForm.commandPlaceholder",
    multiline: true,
  },
  {
    name: "prompt",
    labelKey: "useCases.contributeForm.labelPrompt",
    placeholderKey: "useCases.contributeForm.promptPlaceholder",
    multiline: true,
  },
  {
    name: "expectedResult",
    labelKey: "useCases.contributeForm.labelExpectedResult",
    placeholderKey: "useCases.contributeForm.expectedResultPlaceholder",
    required: true,
  },
  {
    name: "learning",
    labelKey: "useCases.contributeForm.labelLearning",
    placeholderKey: "useCases.contributeForm.learningPlaceholder",
  },
];

export const WorkflowStep: FC<StepProps> = ({ formData, onChange, onNext, onBack, onSaveDraft }) => {
  const updateStep = (index: number, field: keyof UserUseCaseStep, value: string) => {
    const updatedSteps = [...formData.steps];
    updatedSteps[index] = { ...updatedSteps[index], [field]: value };
    onChange({ steps: updatedSteps });
  };

  const addStep = () => {
    if (formData.steps.length < 20) {
      onChange({
        steps: [
          ...formData.steps,
          {
            order: formData.steps.length + 1,
            title: "",
            expectedResult: "",
            codeSnippet: "",
            prompt: "",
            learning: "",
          },
        ],
      });
    }
  };

  const removeStep = (index: number) => {
    if (formData.steps.length > 1) {
      onChange({ steps: formData.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })) });
    }
  };

  const isValid = STEP_VALIDATORS.workflow(formData);

  return (
    <Col gridGap="S400">
      <Text fontSize="T400" fontWeight="bold">
        {getString("useCases.contributeForm.workflow")}
      </Text>

      {WORKFLOW_FIELDS.map((field) => (
        <FormField
          key={field.id}
          config={field}
          value={(formData as any)[field.name]}
          onChange={(v) => onChange({ [field.name]: v })}
        />
      ))}

      <Col gridGap="S200">
        <Text fontSize="T200" fontWeight="bold">
          {getString("useCases.contributeForm.labelStepsToExecute")} *
        </Text>
        <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
          {getString("useCases.contributeForm.stepsDescription")}
        </Text>

        {formData.steps.map((step, index) => (
          <div key={index} className="contribute-use-case-page__step-card">
            {formData.steps.length > 1 && (
              <div className="contribute-use-case-page__step-card-remove">
                <Button
                  variant={ButtonVariant.Tertiary}
                  onClick={() => removeStep(index)}
                  touchpoint={`use-case-remove-step-${index}`}
                >
                  ✕
                </Button>
              </div>
            )}
            <div className="contribute-use-case-page__step-card-header">
              <span className="contribute-use-case-page__step-card-number">
                {getString("useCases.contributeForm.stepNumber", { number: String(step.order) })}
              </span>
              <Input
                touchpoint={`use-case-step-${index}-title`}
                value={step.title}
                onChange={(e: any) => updateStep(index, "title", e.target.value)}
                placeholder={getString("useCases.contributeForm.stepTitlePlaceholder")}
              />
            </div>
            <div className="contribute-use-case-page__step-card-fields">
              {STEP_FIELD_CONFIGS.map(({ name, labelKey, placeholderKey, required, multiline }) => (
                <InputWrapper
                  key={name}
                  id={`step-${index}-${name}`}
                  labelText={getString(labelKey)}
                  required={required}
                >
                  {(inputProps) =>
                    multiline ? (
                      <TextArea
                        {...inputProps}
                        touchpoint={`use-case-step-${index}-${name}`}
                        value={(step[name] as string) || ""}
                        onChange={(e: any) => updateStep(index, name, e.target.value)}
                        placeholder={getString(placeholderKey)}
                        rows={3}
                        resize="vertical"
                        style={{ fontFamily: "monospace", whiteSpace: "pre" }}
                      />
                    ) : (
                      <Input
                        {...inputProps}
                        touchpoint={`use-case-step-${index}-${name}`}
                        value={(step[name] as string) || ""}
                        onChange={(e: any) => updateStep(index, name, e.target.value)}
                        placeholder={getString(placeholderKey)}
                      />
                    )
                  }
                </InputWrapper>
              ))}
            </div>
          </div>
        ))}

        <Button
          variant={ButtonVariant.Tertiary}
          onClick={addStep}
          disabled={formData.steps.length >= 20}
          touchpoint="use-case-add-step"
        >
          {getString("useCases.contributeForm.addStep")}
        </Button>
      </Col>

      <StepActions onBack={onBack} onSaveDraft={onSaveDraft} onNext={onNext} isNextDisabled={!isValid} />
    </Col>
  );
};
